import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { DELETE_ATTACHMENT, UPDATE_DEFECT } from "../../api/mutation";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";
import InputsNew from "../../components/common/InputsNew";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import { uploadFileDirectly } from "../projects/AddProject";
import plusIconuplad from "../../assets/task/plus_icon_uload.svg";
import imgUploadicon from "../../assets/task/imguploadicon.svg";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import { DEFECTS } from "../../api/query";

const severityOptions = [
    {
        key: 101,
        name: "Low"
    },
    {
        key: 102,
        name: "Medium"
    },
    {
        key: 103,
        name: "High"
    },
    {
        key: 104,
        name: "Critical"
    }
]

interface AddDefectConfig {
    title: string;
    severity: String;
    description?: string
    stepsToReproduce: string;
    expectedBehaviour: string;
    actualBehaviour: string;
    // imageurl?: string;
    // fileName?: string;
}

const addDefectSchema = yup.object().shape({
    title: yup.string()
        .test('not-only-whitespace', 'Please add a valid Title', value => !value || value.trim().length > 0)
        .required('This field is required'),
    severity: yup.string().required('This field is required'),
    description: yup.string()
        .test('not-only-whitespace', 'Please add a valid Title', value => !value || value.trim().length > 0).nullable(),
    stepsToReproduce: yup.string()
        .test('not-only-whitespace', 'Please add valid Steps', value => !value || value.trim().length > 0)
        .required('This field is required'),
    expectedBehaviour: yup.string()
        .test('not-only-whitespace', 'Please add valid Expected Behaviour', value => !value || value.trim().length > 0)
        .required('This field is required'),
    actualBehaviour: yup.string()
        .test('not-only-whitespace', 'Please add valid Actual Behaviour', value => !value || value.trim().length > 0)
        .required('This field is required'),
    // imageurl: yup.string(),
    // fileName: yup.string(),
});

const ViewDefect = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate()
    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';

    const location = useLocation()

    const state = location.state as {
        defectID: string
    };

    const { defectID } = state

    // const [imgUrl, setImgUrl] = useState("");
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    const [imgUrls, setImgUrls] = useState<string[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<
        { id: string; file_url: string; file_name: string }[]
    >([]);


    // const [showModal, setShowModal] = useState(false);
    const [modalImage, setModalImage] = useState<string | null>(null);


    // react hook form
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch
    } = useForm<any>({
        resolver: yupResolver(addDefectSchema)
    });


    //api
    const [updateDefect, { loading: updateDefectLoading }] = useMutation(UPDATE_DEFECT, {
        fetchPolicy: 'no-cache',
    })

    const [getDefectByID, { data, loading }] = useLazyQuery(DEFECTS, {
        fetchPolicy: 'no-cache'
    })

    const [deleteAttachment, { loading: deleteAttachmentLoading }] = useMutation(DELETE_ATTACHMENT, {
        fetchPolicy: 'no-cache',
    })

    //useEffect
    useEffect(() => {
        getDefectByID({
            variables: {
                defect_id: parseInt(defectID),
                is_resolved: false
            }
        })
    }, [defectID])

    useEffect(() => {
        if (data && data.defects.status_code === 200) {
            const defect = data.defects.defects[0]

            setValue("title", defect?.title)
            setValue("severity", defect?.severity)
            setValue("stepsToReproduce", defect?.steps_to_reproduce)
            setValue("expectedBehaviour", defect?.expected_behavior)
            setValue("actualBehaviour", defect?.actual_behavior)
            setValue("description", defect?.description)

            if (defect?.attachments_list?.length > 0) {
                const attachments = defect.attachments_list;
                const fileUrls = attachments.map((attachment: any) => attachment.file_url);
                const fileNames = attachments.map((attachment: any) => attachment.file_name);

                // Set the state with the extracted data
                setImgUrls(fileUrls);
                setFileNames(fileNames);

                setExistingAttachments(attachments.map((a: any) => ({
                    id: a.id,
                    file_url: a.file_url,
                    file_name: a.file_name
                })));
            }
        }
    }, data)

    //watch
    const watchSeverity = watch('severity')

    function handleIconClick() {
        fileInputRef.current?.click();
    }

    async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const token = localStorage.getItem("authToken")!;
        const validTypes = ["image/jpeg", "image/png"];
        // const newImageUrls: string[] = [];
        const newUrls: string[] = [];
        const newNames: string[] = [];

        setIsUploading(true);
        setValidationMessage(null);

        for (const file of Array.from(files)) {
            // if (!validTypes.includes(file.type)) {
            //     setValidationMessage("Only JPG and PNG files are allowed.");
            //     setIsUploading(false);
            //     return;
            // }

            try {
                const url = await uploadFileDirectly(file, file.name, token);
                newUrls.push(url);
                newNames.push(file.name);
            } catch (err) {
                console.error("Upload failed", err);
            }
        }

        // setImgUrls((prev) => [...prev, ...newImageUrls]);
        setImgUrls((prev) => [...prev, ...newUrls]);
        setFileNames((prev) => [...prev, ...newNames]);
        setIsUploading(false);

        // If needed, set values in your form for each image (optional)
        // e.g., setValue("imageUrls", [...imgUrls, ...newImageUrls])
    }


    // async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
    //     const file = event.target.files?.[0];
    //     if (!file) return;

    //     // Validate file type
    //     if (!["image/jpeg", "image/png"].includes(file.type)) {
    //         setValidationMessage("Only JPG and PNG files are allowed.");
    //         return;
    //     }

    //     setValidationMessage(null);
    //     setIsUploading(true);

    //     try {
    //         const token = localStorage.getItem("authToken")!;
    //         const url = await uploadFileDirectly(file, file.name, token);
    //         // setImgUrl(url);
    //         setValue("imageurl", url);
    //         setValue("fileName", file.name)
    //     } catch (err) {
    //         console.error("Upload failed", err);
    //     } finally {
    //         setIsUploading(false);
    //     }
    // }

    function handleSelectSeverity(key: number, name: string): void {
        setValue('severity', name)
        trigger('severity')
    }

    async function handleRemoveImage(id: any, index: any) {
        if (id) {
            try {
                const response = await deleteAttachment({ variables: { id } });
                if (response.data.delete_defect_attachment.status_code === 200) {
                    setImgUrls((prev) => prev.filter((_, i) => i !== index));
                    setFileNames((prev) => prev.filter((_, i) => i !== index));
                    showSuccess(response.data.delete_defect_attachment.message);
                } else {
                    showError(response.data.delete_defect_attachment.message);
                }
            } catch (error) {
                if (error instanceof ApolloError) {
                    showError(error.message);
                }
            }
        } else {
            setImgUrls((prev) => prev.filter((_, i) => i !== index));
            setFileNames((prev) => prev.filter((_, i) => i !== index));
        }
    }

    async function onSubmit(data: AddDefectConfig) {
        // const attatchmentList = [
        //     {
        //         file_name: data?.fileName,
        //         file_url: data?.imageurl,
        //         uploaded_by: parseInt(employeeID)
        //     }
        // ]
        const existingUrls = new Set(existingAttachments.map((att) => att.file_url));

        const newAttachmentList = imgUrls
            ?.map((url, index) => ({
                file_name: fileNames[index],
                file_url: url,
                uploaded_by: parseInt(employeeID),
            }))
            .filter((item) => !existingUrls.has(item.file_url));

        const input = {
            title: data.title,
            description: data?.description,
            steps_to_reproduce: data.stepsToReproduce,
            expected_behavior: data.expectedBehaviour,
            actual_behavior: data.actualBehaviour,
            attachment_list: newAttachmentList,
            severity: data.severity,
            created_by: parseInt(employeeID),
            is_issue: true,
            is_confirmed: true,
            confirmed_by: parseInt(employeeID)
        }

        try {
            const response = await updateDefect({ variables: { id: defectID, input: input } });
            if (response.data.update_defect.status_code === 200) {
                showSuccess(response.data.update_defect.message)
                navigate(-1)
            }
            if (response.data.update_defect.status_code === 400) {
                showError(response.data.update_defect.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} id="cts-respon-p-dv" className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    {/* <div className="self-stretch rounded flex flex-col items-start justify-start text-dimgray">
                                <div className="flex flex-row items-center justify-start">
                                    <div className="overflow-hidden flex flex-row items-center justify-start gap-2">
                                        <div className="relative [text-decoration:underline]">Dashboard</div>
                                        <img className="w-4 relative h-4 overflow-hidden shrink-0" alt="" src="Right 16px.svg" />
                                        <div className="relative font-semibold text-gray">Add New Task</div>
                                    </div>
                                </div>
                            </div> */}
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Defect</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        {/* <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5> */}
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsNew
                                contentPlaceholder="Title"
                                label="Title*"
                                type="text"
                                register={register('title')}
                                errorMessage={errors.title}
                            />
                            <SearchableInputsDropdown
                                label="Severity*"
                                initialValue={watchSeverity}
                                placeHolder="--Select--"
                                options={severityOptions}
                                onSelect={handleSelectSeverity}
                                errorMessage={errors?.severity}
                            />
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Description"
                                    label="Description"
                                    type="textarea"
                                    register={register('description')}
                                    errorMessage={errors.description}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Steps to Reproduce"
                                    label="Steps to Reproduce*"
                                    type="textarea"
                                    register={register('stepsToReproduce')}
                                    errorMessage={errors.stepsToReproduce}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Actual Behaviour"
                                    label="Actual Behaviour*"
                                    type="textarea"
                                    register={register('actualBehaviour')}
                                    errorMessage={errors.actualBehaviour}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Expected Behaviour"
                                    label="Expected Behaviour*"
                                    type="textarea"
                                    register={register('expectedBehaviour')}
                                    errorMessage={errors.expectedBehaviour}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                            <div className="rounded flex flex-col items-start justify-start text-left min-w-[22%]">
                                <label className="font-semibold dark:text-white pb-2">Add Attachments</label>

                                <div className="flex flex-wrap gap-4">
                                    {/* Uploaded images */}
                                    {imgUrls.map((url, index) => {
                                        const existing = existingAttachments?.find((att) => att.file_url === url);

                                        return (
                                            <div key={index} className="bg-white p-2 shadow border rounded relative">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(existing?.id, index)}
                                                    className="absolute top-1 right-1 red-clockout-btn-dv cursor-pointer rounded-full py-[2px] px-[2px] shadow hover:bg-red-100 z-10"
                                                    title="Remove image"
                                                >
                                                    <span className="text-red-500 font-bold text-lg text-white">×</span>
                                                </button>

                                                <img
                                                    className="w-36 h-24 object-cover cursor-pointer"
                                                    src={url}
                                                    alt={`Uploaded ${index}`}
                                                    onClick={() => setModalImage(url)}
                                                />

                                                {modalImage && (
                                                    <div
                                                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                                                        onClick={() => setModalImage(null)}
                                                    >
                                                        <img
                                                            alt="Preview"
                                                            src={modalImage}
                                                            className="max-w-full max-h-full object-contain"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )

                                    })}

                                    {/* Upload box or uploading state */}
                                    {isUploading ? (
                                        <div className="w-36 h-24 flex items-center justify-center border border-dashed border-neutral-300">
                                            <span className="text-blue-500">Uploading...</span>
                                        </div>
                                    ) : (
                                        <div
                                            className="w-36 h-24 flex flex-col items-center justify-center border border-dashed border-neutral-300 cursor-pointer"
                                            onClick={handleIconClick}
                                        >
                                            {/* <img src={plusIconuplad} alt="Upload" className="w-6 h-6" /> */}
                                            <div className="cursor-pointer flex flex-col items-start justify-start p-[5px] plus-task-btn taskdash-plush-img rounded">
                                                <img src={plusIconuplad} alt="Upload" className="w-5 h-5" />
                                            </div>
                                            <p className="text-sm">Add more</p>
                                        </div>
                                    )}
                                </div>

                                {/* Hidden file input */}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload}
                                    accept=".jpg,.jpeg,.png"
                                    multiple
                                />

                                {/* Error Message */}
                                {validationMessage && (
                                    <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                                        <span>⚠️</span> {validationMessage}
                                    </p>
                                )}
                            </div>
                            {/* <div className=" rounded flex flex-col items-center justify-start text-left mq1275:min-w-[20%] mq1275:justify-start min-w-[22%]">
                                <label className="self-stretch relative font-semibold dark:text-white pb-2">Add Attachment</label>
                                <div
                                    className={`${imgUrl ? "upload_img_box" : ""} rounded  mq1275:bg-white flex flex-col items-center justify-end p-2.5 box-border max-w-[100%] min-w-[100%] mq950:min-w-full text-center agencyinfo-img-blg mq950:p-0`} id="calltype-row "

                                >
                                    {isUploading ? (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[33px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <div className="text-main-blue">Uploading...</div>
                                            </div>
                                        </div>
                                    ) : imgUrl ? (
                                        <div className="bg-white p-2 shadow-\[0px_1px_2px_rgba\(16\2c _24\2c _40\2c _0\.05\)\] border-surface-border   inner_upload_img_box onborading-bdr-blg">
                                            <img
                                                className="min-w-36 h-[100px] relative overflow-hidden shrink-0 cursor-pointer imgagency-upload"
                                                loading="lazy"
                                                alt=""
                                                src={imgUrl}
                                                onClick={handleIconClick}
                                            />
                                        </div>
                                    ) : (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[35px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <img
                                                    className="w-6 h-6 relative overflow-hidden shrink-0 dark-white-img"
                                                    loading="lazy"
                                                    alt=""
                                                    src={imgUploadicon}
                                                />
                                                <p className="self-stretch relative capitalize my-0">
                                                    Upload
                                                </p>
                                                <div
                                                    className="cursor-pointer rounded plus-task-btn flex flex-row items-center justify-center p-1.5"
                                                    onClick={handleIconClick}
                                                >
                                                    <img
                                                        className="h-5 w-5 relative"
                                                        loading="lazy"
                                                        alt=""
                                                        src={plusIconuplad}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload}
                                    accept=".jpg,.jpeg,.png"
                                    multiple
                                // accept="image/*"
                                />
                                {validationMessage && (
                                    <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                        <img src={''} alt="" />
                                        {validationMessage}
                                    </p>
                                )}
                            </div> */}
                        </div>
                        {/* <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                            <div className=" rounded flex flex-col items-center justify-start text-left mq1275:min-w-[20%] mq1275:justify-start min-w-[22%]">
                                <label className="self-stretch relative font-semibold dark:text-white pb-2">Add Attachment</label>
                                <div
                                    className={`${imgUrl ? "upload_img_box" : ""} rounded  mq1275:bg-white flex flex-col items-center justify-end p-2.5 box-border max-w-[100%] min-w-[100%] mq950:min-w-full text-center agencyinfo-img-blg mq950:p-0`} id="calltype-row "

                                >
                                    {isUploading ? (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[33px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <div className="text-main-blue">Uploading...</div>
                                            </div>
                                        </div>
                                    ) : imgUrl ? (
                                        <div className="bg-white p-2 shadow-\[0px_1px_2px_rgba\(16\2c _24\2c _40\2c _0\.05\)\] border-surface-border inner_upload_img_box onborading-bdr-blg">
                                            <img
                                                className="min-w-36 h-[100px] relative overflow-hidden shrink-0 cursor-pointer imgagency-upload"
                                                loading="lazy"
                                                alt=""
                                                src={imgUrl}
                                                onClick={() => setShowModal(true)}
                                            />

                                            {showModal && (
                                                <div
                                                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                                                    onClick={() => setShowModal(false)}
                                                >
                                                    <img
                                                        src={imgUrl}
                                                        alt="Preview"
                                                        className="max-w-full max-h-full object-contain"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[35px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <img
                                                    className="w-6 h-6 relative overflow-hidden shrink-0 dark-white-img"
                                                    loading="lazy"
                                                    alt=""
                                                    src={imgUploadicon}
                                                />
                                                <p className="self-stretch relative capitalize my-0">
                                                    Upload
                                                </p>
                                                <div
                                                    className="cursor-pointer rounded plus-task-btn flex flex-row items-center justify-center p-1.5"
                                                    onClick={handleIconClick}
                                                >
                                                    <img
                                                        className="h-5 w-5 relative"
                                                        loading="lazy"
                                                        alt=""
                                                        src={plusIconuplad}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload}
                                    accept=".jpg,.jpeg,.png"
                                // accept="image/*"
                                />
                                {validationMessage && (
                                    <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                        <img src={''} alt="" />
                                        {validationMessage}
                                    </p>
                                )}
                            </div>
                        </div> */}
                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                // onClick={handleGoBack}
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </form >
            {(loading || updateDefectLoading || deleteAttachmentLoading) && (
                <Loader />
            )}
        </>
    )
}

export default ViewDefect